import { useEffect, useRef, useState } from "react";
import {
  FieldHelperText,
  FieldProps,
  LabelWithIconAndTooltip,
  getIconForProperty,
  useSnackbarController,
  useStorageSource,
} from "@firecms/core";
import { Button, CircularProgress } from "@firecms/ui";

import { unsavedUploads } from "./unsavedUploads";

const STORAGE_PATH = "blog";
// Matches storage.rules.
const MAX_BYTES = 8 * 1024 * 1024;

/**
 * Photo uploads for a blog post. Stores the download URLs on the post so they
 * can be copied into the markdown and cleaned up from the bucket on save or
 * delete (see collections/uploadCleanup.ts).
 */
export function PostImagesField({
  propertyKey,
  value,
  setValue,
  error,
  showError,
  property,
  includeDescription,
  disabled,
  isSubmitting,
  context,
}: FieldProps<string[]>) {
  const storage = useStorageSource();
  const snackbar = useSnackbarController();
  const input = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(0);

  const images = value ?? [];
  const body: string = context.values.body ?? "";
  const locked = disabled || isSubmitting;

  // Delete never-saved uploads when they leave the list (Delete button or
  // Discard). Uploads that were never in this form are left alone, since they
  // may belong to a local-changes backup FireCMS can still restore.
  const seen = useRef({ entity: context.entityId, images });
  useEffect(() => {
    const before = seen.current;
    seen.current = { entity: context.entityId, images };
    // Switched to a different post.
    if (before.entity !== context.entityId) return;

    for (const url of before.images) {
      if (images.includes(url) || !unsavedUploads.delete(url)) continue;
      storage.deleteFile(url).catch((e) => console.warn(`Could not delete the upload ${url}`, e));
    }
  });

  async function upload(files: FileList | null) {
    if (!files?.length) return;
    const added: string[] = [];
    setUploading(files.length);

    for (const file of Array.from(files)) {
      try {
        if (!file.type.startsWith("image/")) throw new Error("it is not an image");
        if (file.size >= MAX_BYTES) throw new Error("it is larger than 8 MB");

        // Random prefix avoids filename collisions.
        const fileName = `${Math.random().toString(36).slice(2, 7)}_${file.name.replace(/[^\w.-]+/g, "_")}`;
        const { path } = await storage.uploadFile({ file, fileName, path: STORAGE_PATH });
        const { url } = await storage.getDownloadURL(path);
        if (!url) throw new Error("the upload did not return a link");

        unsavedUploads.add(url);
        added.push(url);
      } catch (e) {
        snackbar.open({
          type: "error",
          message: `Could not upload ${file.name}: ${e instanceof Error ? e.message : "unknown error"}`,
        });
      } finally {
        setUploading((n) => n - 1);
      }
    }

    if (added.length) setValue([...images, ...added]);
    if (input.current) input.current.value = "";
  }

  async function copy(url: string) {
    await navigator.clipboard.writeText(url);
    snackbar.open({ type: "success", message: "Link copied. Paste it into the post's image link." });
  }

  // Appends to the end; this field can't see the editor's cursor.
  function addToPost(url: string) {
    context.setFieldValue("body", `${body.trimEnd()}${body.trim() ? "\n\n" : ""}![](${url})\n`);
    snackbar.open({ type: "success", message: "Added to the end of the post." });
  }

  function remove(url: string) {
    if (body.includes(url) && !window.confirm("This photo is used in the post. Removing it will leave a broken image there. Remove it anyway?")) return;

    // File deletion is handled by the effect above, or by cleanup on save.
    setValue(images.filter((u) => u !== url));
  }

  return (
    <>
      <LabelWithIconAndTooltip
        propertyKey={propertyKey}
        icon={getIconForProperty(property, "small")}
        required={property.validation?.required}
        title={property.name}
        className="h-8 text-text-secondary dark:text-text-secondary-dark ml-3.5"
      />

      <div className="rounded-md border border-surface-200 dark:border-surface-700 p-4 flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <input
            ref={input}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={(e) => upload(e.target.files)}
          />
          <Button variant="outlined" size="small" disabled={locked || uploading > 0} onClick={() => input.current?.click()}>
            Upload photos
          </Button>
          {uploading > 0 && (
            <span className="flex items-center gap-2 text-sm text-text-secondary dark:text-text-secondary-dark">
              <CircularProgress size="smallest" /> Uploading {uploading}…
            </span>
          )}
        </div>

        {images.length > 0 && (
          <ul className="grid gap-3 sm:grid-cols-2">
            {images.map((url) => (
              <li key={url} className="flex gap-3 rounded-md border border-surface-200 dark:border-surface-700 p-2">
                <a href={url} target="_blank" rel="noopener noreferrer" className="shrink-0">
                  <img src={url} alt="" className="h-24 w-24 rounded object-cover" />
                </a>
                <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
                  <span className="text-xs text-text-secondary dark:text-text-secondary-dark">
                    {body.includes(url) ? "Used in the post" : "Not in the post yet"}
                  </span>
                  <Button variant="text" size="small" onClick={() => copy(url)}>Copy link</Button>
                  <Button variant="text" size="small" disabled={locked} onClick={() => addToPost(url)}>Add to post</Button>
                  <Button variant="text" size="small" color="error" disabled={locked} onClick={() => remove(url)}>Delete</Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <FieldHelperText
        includeDescription={includeDescription}
        showError={showError}
        error={error}
        disabled={disabled}
        property={property}
      />
    </>
  );
}
